import { call, put, select, takeEvery, takeLatest, cancelled } from 'redux-saga/effects';
import { get, isEmpty, merge } from 'lodash';
import { Router } from '../../src/i18n';
import * as API from '../../utils/API';
import {
  addUserPhone,
  checkUpdatePasswordKeyAction,
  createEntity,
  deleteEntity,
  deleteUserPhone,
  enqueueSnackbarAction,
  getActiveProfileAction,
  getAgenciesAction,
  getAggregationsAction,
  getArtistProfessionsAction,
  getCommonUserData,
  getCompaniesAction,
  getList,
  getOne,
  getProfilePermissionsAction,
  getProfilesAction,
  getSettings,
  getUserPermissionsAction,
  getWholeList,
  loginUser as loginUserAction,
  resetPasswordAction,
  setActiveProfileAction,
  setUserData,
  setUserProfiles,
  userIdentifyAction,
  updateOne,
  updatePasswordAction,
  updateUserData,
  getDynamicCountriesAction,
} from './actions';

import route from '../../constants/routes';
import { selecActiveProfileById, selectAtistProfessions, selectUserData, selectUserProfiles } from './selectors';
import { setIsLoggedInAction } from '../../store/actions';
import { selectCountryByCode } from '../../store/selectors';
import { formatResponseToEntity, formatResponseToList } from '../../utils/crudUtils';
import { setIntervalForPing, fetchAbortController } from '../../utils/common';
import { selectRegistrationProfile } from '../Registration/selectors';
import { setRegistrationProfileAction } from '../Registration/actions';
import { ACCOUNT_SETTINGS, COUNTRIES_ENDPOINT } from '../../constants/endpointConstants';
import { userIdentifyEvent } from '../../utils/tracking';
import { VISIBILITY } from '../../constants/consts';

export function* loginUser({ payload, isAfterRegister = false }) {
  try {
    const userData = yield call(API.login, payload.email, payload.password, payload.isTrustedDevice);
    const { data } = userData;
    yield put(loginUserAction.success(data));
    const token = get(data, 'token');
    yield put(token ? setIsLoggedInAction(true) : setIsLoggedInAction(false));
    setIntervalForPing();
    if (!isAfterRegister) {
      const next = get(payload, 'nextPage', route.HOME);
      Router.push(next);
    }
    yield call(fetchCommonUserData, { payload: { ...data } });
  } catch (err) {
    yield put(loginUserAction.failure(err.message));
    if (payload?.onRejected) {
      payload.onRejected(err);
    }
  } finally {
    yield put(loginUserAction.fulfill());
  }
}

export function* updateUserIdentifyEvent(profileId) {
  const userData = yield call(getUserFromData);
  userIdentifyEvent(userData, profileId);
}

export function* getUserFromData() {
  return yield select(selectUserData());
}

export function* fetchCommonUserData({ payload }) {
  try {
    const userToFetch = payload.user;
    if (!get(userToFetch, 'id', null)) {
      throw new Error('User undefined');
    }
    const id = userToFetch?.id;
    yield call(fetchUserData, id);
    yield call(fetchUserProfiles, id);
    // TODO Pass active profile id
    yield call(fetchActiveProfile, { payload: { userId: id, activeProfileId: payload?.activeProfileId } });
    yield call(getSettingsSaga, id);
  } catch (err) {
    yield put(getCommonUserData.failure(err.message));
  } finally {
    yield put(getCommonUserData.fulfill());
  }
}

export function* getSettingsSaga(userId) {
  yield call(getListSaga, {
    payload: {
      endpoint: ACCOUNT_SETTINGS(userId),
      sagaRoutine: getSettings,
      params: { lang: 'en' },
    },
  });
}

export function* updateUserDataSaga({ payload }) {
  try {
    const user = yield call(getUserFromData);
    if (!get(user, 'id', null)) {
      throw new Error('User undefined');
    }
    const userData = yield call(API.updateUser, get(user, 'id'), payload);
    yield put(updateUserData.success(get(userData, 'data')));
  } catch (err) {
    yield put(updateUserData.failure(err.message));
  } finally {
    yield put(updateUserData.fulfill());
  }
}

export function* addUserPhoneSaga({ payload }) {
  try {
    const user = yield call(getUserFromData);
    const userId = get(user, 'id');
    if (!userId) {
      throw new Error('User undefined');
    }
    const userData = yield call(API.getUser, userId);
    const country = yield select(selectCountryByCode(get(payload, 'countryCode')));
    const phones = get(userData, 'data.phones', []);
    phones.push({
      country: {
        id: get(country, 'id'),
      },
      number: get(payload, 'phone'),
      isActive: phones.length === 0,
    });
    const updatedUserData = yield call(API.updateUser, userId, { phones });
    yield put(addUserPhone.success(get(updatedUserData, 'data')));
  } catch (err) {
    yield put(addUserPhone.failure(err.message));
  } finally {
    yield put(addUserPhone.fulfill());
  }
}

export function* deleteUserPhoneSaga({ payload }) {
  try {
    const user = yield call(getUserFromData);
    const userId = get(user, 'id');
    if (!userId) {
      throw new Error('User undefined');
    }
    const userData = yield call(API.getUser, userId);
    const phones = get(userData, 'data.phones', []);
    const updatedUserData = yield call(API.updateUser, userId, {
      phones: phones.filter(phone => phone.id !== payload.id),
    });
    yield put(deleteUserPhone.success(get(updatedUserData, 'data')));
  } catch (err) {
    yield put(deleteUserPhone.failure(err.message));
  } finally {
    yield put(deleteUserPhone.fulfill());
  }
}

export function* fetchUserData(userId) {
  try {
    const userData = yield call(API.getUser, userId);
    const { data } = userData;
    yield call(updateUserIdentifyEvent, null);
    yield put(setUserData.success(data));
    yield call(getUserPermissions, userId);
  } catch (err) {
    yield put(setUserData.failure(err.message));
  } finally {
    yield put(setUserData.fulfill());
  }
}

export function* fetchUserProfiles(userId) {
  try {
    yield put(setUserProfiles.trigger());
    const userData = yield call(API.getProfiles, userId, true, true);
    const profiles = get(userData, 'data.data');
    yield put(setUserProfiles.success(profiles));
    // TODO Remove when Registration 2.0 will be there
    const regProfile = yield select(selectRegistrationProfile());
    if (regProfile) {
      yield put(setRegistrationProfileAction.success(profiles.find(p => p.id === regProfile.id)));
    }
  } catch (err) {
    yield put(setUserProfiles.failure(err.message));
  } finally {
    yield put(setUserProfiles.fulfill());
  }
}

export function* fetchActiveProfile({ payload }) {
  try {
    const settingsData = yield call(API.getActiveProfile, payload.userId);
    const userProfiles = yield select(selectUserProfiles());
    let activeProfileId = get(settingsData, 'data.value') || payload?.activeProfileId;

    if (!userProfiles || userProfiles.length === 0) {
      yield call(getProfilePermissions);
      throw new Error('No active profile available');
    }

    if (!userProfiles.find(up => up.id == activeProfileId)) {
      activeProfileId = get(userProfiles, '[0].id');
      // TODO Save active_id on api?
    }

    const activeProfile = yield call(API.getProfileData, activeProfileId);
    yield call(getProfilePermissions, get(activeProfile, 'data.id'));
    yield put(getActiveProfileAction.success(activeProfile?.data));
  } catch (err) {
    yield put(getActiveProfileAction.failure(err.message));
  } finally {
    yield put(getActiveProfileAction.fulfill());
  }
}

export function* updateActiveProfile({ payload }) {
  try {
    const userData = yield call(getUserFromData);
    const userId = get(userData, 'id');
    const response = yield call(API.updateSettingsField, userId, 'active_profile_id', payload.profileId);
    const profileId = get(response, 'data.value', null);
    yield call(updateUserIdentifyEvent, profileId);
    const activeProfileData = yield select(selecActiveProfileById(profileId));
    yield call(getProfilePermissions, get(activeProfileData, 'id'));
    yield put(setActiveProfileAction.success(activeProfileData));
    if (payload.path && payload.as) {
      Router.push(payload.path, payload.as);
    } else if (typeof window !== 'undefined') {
      window.location.reload();
    }
  } catch (err) {
    yield put(setActiveProfileAction.failure(err.message));
  } finally {
    yield put(setActiveProfileAction.fulfill());
  }
}

export function* getProfilePermissions(profileId) {
  try {
    if (!profileId) {
      yield put(getProfilePermissionsAction.failure('No profile Id'));
      return;
    }
    const response = yield call(API.getProfilePermissions, profileId);
    yield put(getProfilePermissionsAction.success(get(response, 'data.data', [])));
  } catch (err) {
    yield put(getProfilePermissionsAction.failure(err.message));
  } finally {
    yield put(getProfilePermissionsAction.fulfill());
  }
}

export function* getUserPermissions(userId) {
  try {
    const response = yield call(API.getUserPermissions, userId);
    yield put(getUserPermissionsAction.success(get(response, 'data.data', [])));
  } catch (err) {
    yield put(getUserPermissionsAction.failure(err.message));
  } finally {
    yield put(getUserPermissionsAction.fulfill());
  }
}

export function* resetPassword({ payload }) {
  try {
    yield call(API.resetUserPassword, payload);
    yield put(resetPasswordAction.success());
  } catch (err) {
    yield put(resetPasswordAction.failure(err.message));
  } finally {
    yield put(resetPasswordAction.fulfill());
  }
}

export function* updatePassword({ payload }) {
  try {
    yield call(API.updateUserPassword, payload.key, payload.password);
    yield put(updatePasswordAction.success());
  } catch (err) {
    yield put(updatePasswordAction.failure(err.message));
  } finally {
    yield put(updatePasswordAction.fulfill());
  }
}

export function* checkPasswordKey({ payload }) {
  try {
    yield call(API.checkPasswordKeyStatus, payload);
    yield put(checkUpdatePasswordKeyAction.success());
  } catch (err) {
    yield put(checkUpdatePasswordKeyAction.failure(err.message));
  } finally {
    yield put(checkUpdatePasswordKeyAction.fulfill());
  }
}

export function* fetchArtistProfessions({ payload }) {
  try {
    const { query } = payload;
    const professions = yield select(selectAtistProfessions());
    if (isEmpty(professions)) {
      const response = yield call(API.getArtistProfessions, query || payload);
      yield put(getArtistProfessionsAction.success(get(response, 'data.data')));
    }
  } catch (err) {
    yield put(getArtistProfessionsAction.failure(err.message));
  } finally {
    yield put(getArtistProfessionsAction.fulfill());
  }
}

export function* fetchAggregations({ payload }) {
  const { query, pagination, endpoint } = payload;
  const abortController = fetchAbortController();
  const signal = abortController ? abortController.signal : null;
  try {
    const response = yield call(API.getAggregations, endpoint, query, pagination, signal);
    yield put(getAggregationsAction.success(get(response, 'data')));
  } catch (err) {
    if (yield cancelled() && abortController) {
      abortController.abort();
    }
    yield put(getAggregationsAction.failure(err.message));
  } finally {
    if (yield cancelled() && abortController) {
      // Cancel the API call if the saga was cancelled
      abortController.abort();
    }
    yield put(getAggregationsAction.fulfill());
  }
}

export function* fetchAgencies({ payload }) {
  try {
    const response = yield call(API.getAgencies, { query: payload.query || payload, ...get(payload, 'query', {}) });
    yield put(getAgenciesAction.success(get(response, 'data.data')));
  } catch (err) {
    yield put(getAgenciesAction.failure(err.message));
  } finally {
    yield put(getAgenciesAction.fulfill());
  }
}

export function* fetchCompanies({ payload }) {
  try {
    const response = yield call(API.getCompanies, payload.query, payload.pagination);
    yield put(getCompaniesAction.success(get(response, 'data.data')));
  } catch (err) {
    yield put(getCompaniesAction.failure(err.message));
  } finally {
    yield put(getCompaniesAction.fulfill());
  }
}

export function* fetchProfilesList({ payload }) {
  try {
    const response = yield call(
      API.searchProfiles,
      payload.type,
      payload.query,
      payload.pagination,
      payload.filters,
      payload.from,
    );
    yield put(getProfilesAction.success(get(response, 'data.data')));
  } catch (err) {
    yield put(getProfilesAction.failure(err.message));
  } finally {
    yield put(getProfilesAction.fulfill());
  }
}

export function* getListSaga({ payload }) {
  const { endpoint, sagaRoutine, params, callback, notifyOnError } = payload;
  if (endpoint === null || sagaRoutine === null) {
    throw new Error('Specify endpoint and sagaRoutine');
  }
  yield put(sagaRoutine.trigger(payload));
  try {
    yield put(sagaRoutine.request());
    const response = yield call(API.getList, endpoint, params);
    const list = formatResponseToList(response);
    yield put(sagaRoutine.success(list));
    if (Boolean(callback) && typeof callback === 'function') callback(list);
  } catch (err) {
    if (notifyOnError) {
      yield put(
        enqueueSnackbarAction({
          message: `Error fetching ${endpoint} data: ${err.message}`,
          options: {
            variant: 'error',
          },
        }),
      );
    }
    yield put(sagaRoutine.failure(err.message));
  } finally {
    yield put(sagaRoutine.fulfill());
  }
}

export function* getWholeListSaga({ payload }) {
  const payloadWithPagination = merge(payload, { params: { pagination: { page: 1, limit: 10000 } } });
  yield call(getListSaga, { payload: payloadWithPagination });
}

export function* getOneSaga({ payload }) {
  const { endpoint, sagaRoutine, id, params } = payload;
  if (endpoint === null || sagaRoutine === null || id === null) {
    throw new Error('Specify endpoint, id, sagaRoutine');
  }
  yield put(sagaRoutine.trigger());
  try {
    yield put(sagaRoutine.request());
    const response = yield call(API.getOne, endpoint, id, params);
    const data = formatResponseToEntity(response);
    yield put(sagaRoutine.success(data));
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: `Error fetching ${endpoint} data: ${err.message}`,
        options: {
          variant: 'error',
        },
      }),
    );
    yield put(sagaRoutine.failure(err.message));
  } finally {
    yield put(sagaRoutine.fulfill());
  }
}

export function* updateOneSaga({ payload }) {
  const { endpoint, sagaRoutine, id, params, files, successMessage, errorMessage, callback } = payload;
  if (endpoint === null || id === null) {
    throw new Error('Specify endpoint, id, sagaRoutine');
  }
  if (sagaRoutine) yield put(sagaRoutine.trigger(payload));
  try {
    if (sagaRoutine) yield put(sagaRoutine.request());
    const response = yield call(API.updateOne, endpoint, id, params, files);
    const data = formatResponseToEntity(response);
    if (sagaRoutine) yield put(sagaRoutine.success(data));
    if (Boolean(callback) && typeof callback === 'function') callback(data?.entity);
    yield put(
      enqueueSnackbarAction({
        message: successMessage || `Success updating /${endpoint} data`,
        options: {
          variant: 'success',
        },
      }),
    );
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: errorMessage || `Error updating /${endpoint} data: ${err.message}`,
        options: {
          variant: 'error',
        },
      }),
    );
    if (sagaRoutine) yield put(sagaRoutine.failure(err.message));
  } finally {
    if (sagaRoutine) yield put(sagaRoutine.fulfill());
  }
}

export function* deleteOneSaga({ payload }) {
  const { endpoint, sagaRoutine, id, params, successMessage, errorMessage, callback } = payload;
  if (endpoint === null || id === null) {
    throw new Error('Specify endpoint, id');
  }
  if (sagaRoutine) {
    yield put(sagaRoutine.trigger(payload));
  }
  try {
    if (sagaRoutine) {
      yield put(sagaRoutine.request());
    }
    yield call(API.deleteOne, endpoint, id, params);
    if (sagaRoutine) {
      yield put(sagaRoutine.success({ id }));
    }
    if (Boolean(callback) && typeof callback === 'function') callback(id);
    yield put(
      enqueueSnackbarAction({
        message: successMessage || `Success deleting /${endpoint} data`,
        options: {
          variant: 'success',
        },
      }),
    );
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: errorMessage || `Error deleting /${endpoint} data: ${err.message}`,
        options: {
          variant: 'error',
        },
      }),
    );
    if (sagaRoutine) {
      yield put(sagaRoutine.failure(err.message));
    }
  } finally {
    if (sagaRoutine) {
      yield put(sagaRoutine.fulfill());
    }
  }
}

export function* createEntitySaga({ payload }) {
  const { endpoint, sagaRoutine, params, successMessage, errorMessage, callback, files } = payload;
  if (endpoint === null) {
    throw new Error('Specify endpoint, id');
  }
  if (sagaRoutine) {
    yield put(sagaRoutine.trigger(payload));
  }
  try {
    if (sagaRoutine) {
      yield put(sagaRoutine.request());
    }
    const response = yield call(API.create, endpoint, params, files);
    const data = formatResponseToEntity(response);
    if (sagaRoutine) {
      yield put(sagaRoutine.success(data));
    }

    if (Boolean(callback) && typeof callback === 'function') callback(data?.entity);

    if (!payload?.noSuccessMessage) {
      yield put(
        enqueueSnackbarAction({
          message: successMessage || `Success created /${endpoint} data`,
          options: {
            variant: 'success',
          },
        }),
      );
    }
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: errorMessage || err?.message?.message || err?.message || `Error created /${endpoint} data`,
        options: {
          variant: 'error',
        },
      }),
    );
    if (sagaRoutine) {
      yield put(sagaRoutine.failure(err.message));
    }
  } finally {
    if (sagaRoutine) {
      yield put(sagaRoutine.fulfill());
    }
  }
}

export function* fetchDynamicCountries({ payload }) {
  try {
    const response = yield call(API.getList, COUNTRIES_ENDPOINT, {
      queryParams: { show_invalid: true, visibility: VISIBILITY.ALL, ...(payload?.params || {}) },
    });
    yield put(getDynamicCountriesAction.success(formatResponseToList(response)));
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: `Error fetching ${COUNTRIES_ENDPOINT} data: ${err.message}`,
        options: {
          variant: 'error',
        },
      }),
    );
    yield put(getDynamicCountriesAction.failure(err.message));
  } finally {
    yield put(getDynamicCountriesAction.fulfill());
  }
}

export default function* appSaga() {
  yield takeLatest(loginUserAction.TRIGGER, loginUser);
  yield takeLatest(getCommonUserData.TRIGGER, fetchCommonUserData);
  yield takeLatest(updateUserData.TRIGGER, updateUserDataSaga);
  yield takeLatest(addUserPhone.TRIGGER, addUserPhoneSaga);
  yield takeLatest(deleteUserPhone.TRIGGER, deleteUserPhoneSaga);
  yield takeLatest(setActiveProfileAction.TRIGGER, updateActiveProfile);
  yield takeLatest(resetPasswordAction.TRIGGER, resetPassword);
  yield takeLatest(updatePasswordAction.TRIGGER, updatePassword);
  yield takeLatest(checkUpdatePasswordKeyAction.TRIGGER, checkPasswordKey);
  yield takeLatest(getArtistProfessionsAction.TRIGGER, fetchArtistProfessions);
  yield takeLatest(getAggregationsAction.TRIGGER, fetchAggregations);
  yield takeLatest(getAgenciesAction.TRIGGER, fetchAgencies);
  yield takeLatest(getCompaniesAction.TRIGGER, fetchCompanies);
  yield takeLatest(getProfilesAction.TRIGGER, fetchProfilesList);
  yield takeEvery(getList.TRIGGER, getListSaga);
  yield takeEvery(getOne.TRIGGER, getOneSaga);
  yield takeEvery(updateOne.TRIGGER, updateOneSaga);
  yield takeEvery(deleteEntity.TRIGGER, deleteOneSaga);
  yield takeEvery(createEntity.TRIGGER, createEntitySaga);
  yield takeEvery(getWholeList.TRIGGER, getWholeListSaga);
  yield takeEvery(userIdentifyAction.TRIGGER, updateUserIdentifyEvent);
  yield takeLatest(getActiveProfileAction.TRIGGER, fetchActiveProfile);
  yield takeLatest(getDynamicCountriesAction.TRIGGER, fetchDynamicCountries);
}
